<template>
  <div class="join-page">
    <!-- Banner 区域：文案已内嵌在图片中，尺寸与关于我们等页一致 -->
    <section class="banner">
      <div class="flx-center">
        <img
          v-show="activeTab === 'social'"
          class="banner-img"
          :src="socialBannerSrc"
          alt="社会招聘"
        >
        <img
          v-show="activeTab === 'campus'"
          class="banner-img"
          :src="campusBannerSrc"
          alt="校园招聘"
        >
      </div>
    </section>

    <!-- Tab 导航 -->
    <div class="tab-nav">
      <div class="tab-container">
        <button class="tab-item" :class="{ active: activeTab === 'social' }" @click="switchTab('social')">社会招聘</button>
        <button class="tab-item" :class="{ active: activeTab === 'campus' }" @click="switchTab('campus')">校园招聘</button>
      </div>
    </div>

    <!-- 内容区域 -->
    <main class="content">
      <!-- ================== 社会招聘 ================== -->
      <div v-if="activeTab === 'social'" class="tab-content active">
        <!-- Step 1: Job Categories -->
        <div v-if="socialStep === 'categories'">
          <div v-if="isMobile" class="search-box">
            <el-input
              v-model="socialSearchKeyword"
              placeholder="搜索岗位名称或类别"
              prefix-icon="el-icon-search"
              clearable
            />
          </div>
          <div
            v-if="filteredSocialCategories.length === 0 && filteredSocialGlobalJobs.length === 0"
            class="empty-state"
          >暂无相关招聘岗位</div>
          <template v-else>
            <!-- 搜索命中岗位：展示岗位 -->
            <div v-if="filteredSocialGlobalJobs.length" class="search-result-group">
              <h3 v-if="isSocialSearching" class="search-result-title">匹配岗位</h3>
              <div class="job-grid">
                <div
                  v-for="(item, index) in filteredSocialGlobalJobs"
                  :key="'job-' + item.id + '-' + item.childrenId + '-' + index"
                  class="job-card"
                  @click="selectSocialJobFromSearch(item)"
                >
                  <div class="job-icon" v-html="getSocialListIcon(item.id)"></div>
                  <div class="job-info">
                    <h3>{{ item.title }}</h3>
                    <p>{{ getSocialCategoryTitle(item.id) }} · {{ item.address }}</p>
                  </div>
                </div>
              </div>
            </div>
            <!-- 搜索命中类别 / 未搜索：展示类别 -->
            <div v-if="filteredSocialCategories.length" class="search-result-group">
              <h3 v-if="isSocialSearching" class="search-result-title">匹配类别</h3>
              <div class="job-grid">
                <div
                  v-for="(item, index) in filteredSocialCategories"
                  :key="'cat-' + item.id + '-' + index"
                  class="job-card"
                  @click="selectSocialCategory(item, index)"
                >
                  <div class="job-icon" v-html="getSocialIcon(item.id)"></div>
                  <div class="job-info">
                    <h3>{{ item.title }}</h3>
                    <p>{{ item.subtitle }}</p>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </div>

        <!-- Step 2: Job List -->
        <div v-else-if="socialStep === 'list'">
          <a class="back-link" @click="backToSocialCategories">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            返回
          </a>
          <div v-if="filteredSocialJobs.length === 0" class="empty-state">暂无招聘岗位</div>
          <div v-else class="job-grid">
            <div
              v-for="(item, index) in filteredSocialJobs"
              :key="index"
              class="job-card"
              @click="selectSocialJob(item, index)"
            >
              <div class="job-icon" v-html="getSocialListIcon(item.id)"></div>
              <div class="job-info">
                <h3>{{ item.title }}</h3>
                <p>{{ item.address }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 3: Job Detail -->
        <div v-else-if="socialStep === 'detail'">
          <a class="back-link" @click="backFromSocialDetail">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            返回
          </a>
          <div class="content-card">
            <h1 class="job-title">{{ socialJobDetail.main_title }}</h1>
            <div v-for="(section, si) in socialJobDetail.main_content" :key="si" class="job-section">
              <h2 class="job-section-title">{{ section.title }}</h2>
              <div class="job-section-content">
                <ol>
                  <li v-for="(line, li) in section.content" :key="li">{{ line.replace(/^\d+[、]/, '') }}</li>
                </ol>
              </div>
            </div>
            <div class="apply-section">
              <button type="button" class="apply-btn" @click="openResumeModal">立即投递</button>
              <button type="button" class="apply-btn apply-btn-secondary" @click="openStatusModal">查看投递状态</button>
            </div>
          </div>
        </div>
      </div>

      <!-- ================== 校园招聘 ================== -->
      <div v-if="activeTab === 'campus'" class="tab-content active">
        <!-- Step 1: Campus Categories -->
        <div v-if="campusStep === 'categories'">
          <div v-if="isMobile" class="search-box">
            <el-input
              v-model="campusSearchKeyword"
              placeholder="搜索岗位名称"
              prefix-icon="el-icon-search"
              clearable
            />
          </div>
          <div v-if="filteredCampusCategories.length === 0" class="empty-state">暂无相关招聘岗位</div>
          <div v-else class="job-grid campus-grid">
            <div
              v-for="(item, index) in filteredCampusCategories"
              :key="index"
              class="job-card"
              @click="selectCampusCategory(item, index)"
            >
              <div class="job-icon" v-html="getCampusIcon(item.id)"></div>
              <div class="job-info">
                <h3>{{ item.title }}</h3>
                <p>{{ item.subtitle }}</p>
              </div>
            </div>
          </div>
        </div>

        <!-- Step 2: Campus Detail -->
        <div v-else>
          <a class="back-link" @click="campusStep = 'categories'">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
            返回
          </a>
          <div class="content-card">
            <h1 class="job-title">{{ campusJobDetail.title }}</h1>
            <div v-for="(section, si) in campusJobDetail.sections" :key="si" class="job-section">
              <h2 v-if="section.title" class="job-section-title">{{ section.title }}</h2>
              <div class="job-section-content">
                <ol>
                  <li v-for="(line, li) in section.content" :key="li">{{ line.replace(/^\d+[、]/, '') }}</li>
                </ol>
              </div>
            </div>
            <div class="apply-section">
              <button type="button" class="apply-btn" @click="openResumeModal">立即投递</button>
              <button type="button" class="apply-btn apply-btn-secondary" @click="openStatusModal">查看投递状态</button>
            </div>
          </div>
        </div>
      </div>
    </main>

    <!-- 投递简历弹窗 -->
    <div class="modal-overlay" :class="{ active: showResumeForm }" @click="closeResumeModal">
      <div class="join-modal" @click.stop>
        <!-- 表单内容 -->
        <div v-if="!resumeSubmitted" class="form-content">
          <div class="modal-header">
            <h3 class="modal-title">投递简历</h3>
            <button class="modal-close" @click="showResumeForm = false">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M18 6L6 18M6 6l12 12"/>
              </svg>
            </button>
          </div>
          <div class="modal-body">
            <form ref="resumeForm" @submit.prevent="submitResume">
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">姓名<span class="required">*</span></label>
                  <input type="text" class="form-input" v-model="resumeData.name" placeholder="请输入您的姓名" required>
                </div>
                <div class="form-group">
                  <label class="form-label">联系电话<span class="required">*</span></label>
                  <input type="tel" class="form-input" v-model="resumeData.phone" placeholder="请输入手机号码" required pattern="1[3-9]\d{9}">
                </div>
              </div>
              <div class="form-row">
                <div class="form-group">
                  <label class="form-label">学历<span class="required">*</span></label>
                  <select class="form-select" v-model="resumeData.education" required>
                    <option value="">请选择学历</option>
                    <option value="大专">大专</option>
                    <option value="本科">本科</option>
                    <option value="硕士">硕士</option>
                  </select>
                </div>
                <div class="form-group">
                  <label class="form-label">学校<span class="required">*</span></label>
                  <select
                    v-if="activeTab === 'campus'"
                    class="form-select"
                    v-model="resumeData.school"
                    required
                  >
                    <option value="">请选择学校</option>
                    <option v-for="school in campusSchoolOptions" :key="school" :value="school">{{ school }}</option>
                  </select>
                  <input
                    v-else
                    type="text"
                    class="form-input"
                    v-model="resumeData.school"
                    placeholder="请输入学校名称"
                    required
                  >
                </div>
              </div>
              <div class="form-group">
                <label class="form-label">专业<span class="required">*</span></label>
                <input type="text" class="form-input" v-model="resumeData.major" placeholder="请输入专业名称" required>
              </div>
              <div class="form-group">
                <label class="form-label">简历附件<span class="required">*</span></label>
                <div
                  class="upload-area"
                  :class="{ 'has-file': uploadedFile }"
                  @click="triggerFileSelect"
                  @dragover.prevent="onDragOver"
                  @dragleave.prevent="onDragLeave"
                  @drop.prevent="onDrop"
                >
                  <svg class="upload-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    <polyline points="17 8 12 3 7 8"/>
                    <line x1="12" y1="3" x2="12" y2="15"/>
                  </svg>
                  <p class="upload-text">点击或拖拽上传简历</p>
                  <p class="upload-hint">仅支持 1 个文件，PDF / Word，不超过 10MB</p>
                </div>
                <input type="file" class="file-input" ref="fileInput" accept=".pdf,.doc,.docx" @change="handleFileSelect">
                <div class="file-info" v-if="uploadedFile">
                  <div class="file-icon">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14 2 14 8 20 8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                    </svg>
                  </div>
                  <div class="file-details">
                    <div class="file-name">{{ uploadedFile.name }}</div>
                    <div class="file-size">{{ formatFileSize(uploadedFile.size) }}</div>
                  </div>
                  <button type="button" class="file-remove" @click="removeFile">
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                      <path d="M18 6L6 18M6 6l12 12"/>
                    </svg>
                  </button>
                </div>
              </div>
              <button type="submit" class="submit-btn" :disabled="submitting">
                {{ submitting ? '提交中...' : '确认投递' }}
              </button>
            </form>
          </div>
        </div>
        <!-- 成功提示 -->
        <div v-else class="success-message show">
          <div class="success-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
          </div>
          <h3 class="success-title">投递成功！</h3>
          <p class="success-text">您的简历已提交，HR收到简历后会尽快与您联系</p>
          <button class="success-btn" @click="closeResumeModal">知道了</button>
        </div>
      </div>
    </div>

    <!-- 查看投递状态弹窗 -->
    <div class="modal-overlay" :class="{ active: showStatusModal }" @click="closeStatusModal">
      <div class="join-modal status-modal" @click.stop>
        <div class="modal-header">
          <h3 class="modal-title">查看投递状态</h3>
          <button type="button" class="modal-close" @click="closeStatusModal">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="modal-body">
          <p class="status-tip">请输入投递时填写的姓名和手机号，查询您的简历处理进度</p>
          <form class="status-query-form" @submit.prevent="queryResumeStatus">
            <div class="status-query-fields">
              <div class="form-group">
                <label class="form-label">姓名<span class="required">*</span></label>
                <input
                  type="text"
                  class="form-input"
                  v-model="statusQuery.name"
                  placeholder="请输入姓名"
                  required
                >
              </div>
              <div class="form-group">
                <label class="form-label">手机号<span class="required">*</span></label>
                <input
                  type="tel"
                  class="form-input"
                  v-model="statusQuery.phone"
                  placeholder="请输入11位手机号"
                  required
                  pattern="1[3-9]\d{9}"
                >
              </div>
            </div>
            <button type="submit" class="submit-btn status-query-btn" :disabled="statusQuerying">
              {{ statusQuerying ? '查询中...' : '查询状态' }}
            </button>
          </form>

          <div v-if="statusQueried" class="status-result">
            <div class="status-result-title">
              查询结果
              <span v-if="statusList.length">共 {{ statusList.length }} 条</span>
            </div>
            <div v-if="statusList.length === 0" class="status-empty">
              <p>未查询到相关投递记录</p>
              <p class="status-empty-hint">请确认姓名、手机号与投递时一致</p>
            </div>
            <div v-else class="status-list">
              <div v-for="(item, index) in statusList" :key="index" class="status-card">
                <div class="status-card-header">
                  <div class="status-card-main">
                    <h4 class="status-position">{{ item.applyPosition || '未知职位' }}</h4>
                  </div>
                  <span class="status-tag" :class="'status-' + (item.handleStatus || '').toLowerCase()">
                    {{ formatHandleStatus(item.handleStatus) }}
                  </span>
                </div>
                <div class="status-card-body">
                  <div class="status-row">
                    <span>姓名</span>
                    <em>{{ item.name || '-' }}</em>
                  </div>
                  <div class="status-row">
                    <span>联系电话</span>
                    <em>{{ item.phone || '-' }}</em>
                  </div>
                  <div class="status-row">
                    <span>学历</span>
                    <em>{{ item.education || '-' }}</em>
                  </div>
                  <div class="status-row">
                    <span>学校</span>
                    <em>{{ item.school || '-' }}</em>
                  </div>
                  <div class="status-row">
                    <span>专业</span>
                    <em>{{ item.major || '-' }}</em>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { submitResume, querySelfResumeStatus } from '@/api/modules/campus'
import { JOIN_BANNER_URLS, preloadJoinBanners } from '@/utils/bannerPreload'
import { useOssUpload } from '@/utils/useOssUpload'

const { aliOssUpload } = useOssUpload()

export default {
  name: 'JoinPage',
  data() {
    return {
      activeTab: 'social',
      isMobile: false,
      socialSearchKeyword: '',
      socialListSearchKeyword: '',
      campusSearchKeyword: '',

      // Social recruitment state
      socialStep: 'categories',
      socialActiveCategoryIndex: 0,
      socialActiveJobIndex: 0,
      selectedSocialCategory: null,
      selectedSocialJob: null,
      // 从搜索结果进入详情时，返回直接回到类别页
      socialEnteredFromSearch: false,
      inviteCategories: [
        { id: '1', title: '仓储类', subtitle: '仓库管理 / 库存控制 / 物流协调' },
        { id: '2', title: '海运操作类', subtitle: '海运订舱 / 单证处理 / 客户服务' },
        { id: '3', title: '空运操作类', subtitle: '空运订舱 / 货物跟踪 / 异常处理' },
        { id: '4', title: '海运产品类', subtitle: '产品设计 / 市场分析 / 供应商管理' },
        { id: '5', title: '财务类', subtitle: '会计核算 / 财务分析 / 资金管理' },
        { id: '6', title: '研发类', subtitle: '软件开发 / 系统架构 / 数据分析' },
        { id: '7', title: '人力资源类', subtitle: '招聘配置 / 培训发展 / 薪酬绩效' },
        { id: '8', title: '海外仓类', subtitle: '海外运营 / 仓储管理 / 本地化服务' },
        { id: '9', title: '销售类', subtitle: '客户开发 / 商务谈判 / 销售管理' },
        { id: '10', title: '客服类', subtitle: '客户咨询 / 投诉处理 / 服务优化' },
        { id: '11', title: '采购/理赔类', subtitle: '供应商管理 / 成本控制 / 理赔处理' },
        { id: '12', title: '总经办', subtitle: '战略规划 / 行政管理 / 高层支持' },
        { id: '13', title: '市场品牌类', subtitle: '品牌推广 / 市场活动 / 公关传播' }
      ],
      socialJobList: [
        { id: '9', childrenId: '1', address: '深圳、东莞、广州、青岛、合肥、佛山、郑州、中山、上海、厦门、义乌', content: '大专 | 经验不限', title: '跨境物流销售' },
        { id: '9', childrenId: '2', address: '深圳', content: '大专以上学历，英语四级以上水平；| 具有FBA物流行业操作客服经验，半年以上；', title: '业务助理' },
        { id: '10', childrenId: '1', address: '深圳、长沙', content: '大专及以上学历，国际贸易，物流类相关专业优先；| 有半年/1年以上的FBA、FOB物流经验者优先；', title: '港前客服' },
        { id: '10', childrenId: '2', address: '长沙', content: '本科及以上学历，国际贸易，物流类相关专业优先；| 经验不限', title: '客服助理' },
        { id: '5', childrenId: '1', address: '长沙', content: '大专及以上学历 | 一年以上成本结算岗位的工作经验，熟练使用表格', title: '成本结算会计' },
        { id: '5', childrenId: '2', address: '长沙', content: '大专以上学历，财会相关专业毕业 | 一年以上相关岗位工作经验', title: '应收会计' },
        { id: '4', childrenId: '1', address: '深圳', content: '本科学历，英语4级以上 | 5-10年同行业工作经验', title: '产品经理（总监）' },
        { id: '4', childrenId: '2', address: '深圳、广州、上海、义乌', content: '有传统货代商务/市场相关岗位2年以上工作经验', title: '海运项目经理' },
        { id: '2', childrenId: '1', address: '深圳', content: '本科及以上学历，3年及以上欧线全程操作经验', title: '海运操作经理' },
        { id: '2', childrenId: '2', address: '深圳', content: '大专及以上学历，2年以上货代操作文件工作经验', title: '海运操作' },
        { id: '3', childrenId: '1', address: '深圳', content: '本科及以上学历，进出口贸易、物流等相关专业优先', title: '空运操作' },
        { id: '3', childrenId: '2', address: '深圳、长沙', content: '本科及以上学历，国际贸易，物流管理相关专业优先', title: '2022-2024届本科生（储备干部方向）' },
        { id: '8', childrenId: '1', address: '深圳', content: '本科以上学历，3年以上客服工作经验', title: '海外仓客服经理' },
        { id: '8', childrenId: '2', address: '深圳', content: '熟悉信息化系统，熟悉海外仓储系统', title: '系统运维专员' },
        { id: '8', childrenId: '3', address: '深圳', content: '本科以上学历，3年以上仓储/流行业经验', title: '海外仓质控' }
      ],
      socialJobDetails: [
        {
          id: '9', childrenId: '1', main_title: '跨境物流销售',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、利用平台、网络、电话、网站等营销方法，开发对跨境电商物流或国际物流有需求的客户；',
                '2、合理利用和维护公司客户资源，通过各种途径开发新客户，为客户提供国际专线物流、跨境电商小包、海外仓储等服务；',
                '3、做好市场信息的收集，及时发现工作中的问题并反馈异常，跟进异常的处理结果；',
                '4、部门领导安排的其它临时性工作。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、大专以上学历，专业不限；',
                '2、熟悉国际物流、货代或跨境电商行业，有出口国际空运、FBA跨境物流、国际物流小包或有FBA相关销售经验优先；',
                '3、积极热情，责任心强，良好的沟通协调能力及客户服务意识。',
                '4、对销售工作有兴趣的优秀应届生也可考虑。',
                '办公地点：深圳、东莞、广州、青岛、合肥、佛山、郑州、中山、上海、厦门、义乌'
              ]
            }
          ]
        },
        {
          id: '9', childrenId: '2', main_title: '业务助理',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责系统下单，跟进运单交期，物流跟踪情况及时向业务反馈情况。',
                '2、协助业务收集航班报表，发货进度表，及日常工作汇报情况日报、周报、月报。',
                '3、整理归类客户资料文件及合同文本存档流程，并做好保密工作。',
                '4、查看每天的出货情况，如货量有异常要及时找客户了解情况并反馈给领导。',
                '5、协助销售人员日常工作，衔接项目部、操作部、客服部处理客户的相关需求形成闭环。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、大专以上学历，英语四级以上水平；',
                '2、具有FBA物流行业操作客服经验，半年以上；',
                '3、能够熟练使用excel等办公软件，对数据较为敏感；',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '10', childrenId: '1', main_title: '港前客服',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、主要负责FBA空派，海派，铁路、卡航、快递等订单的跟进与处理；',
                '2、接收订单/指引下单/订单审核/提货/数据确认/船期确认/物流节点跟踪与反馈/问题件跟进与处理等事宜；',
                '3、了解公司产品与服务，及时为客户提供服务咨询及报价服务，解答客户疑问；',
                '4、与销售同事良好协作，配合销售同事需求，共同服务好客户；',
                '5、与相关部门进行工作协调，及时解决问题；',
                '6、维护好客户关系，增加黏性，提高货量；',
                '7、完成上级领导交代的其他工作。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、熟练使用办公软件操作；保证日常线上交流沟通；',
                '2、有半年/1年以上的FBA、FOB物流经验者优先；',
                '3、大专及以上学历，国际贸易，物流类相关专业优先；',
                '4、灵活，工作认真，细致，责任心强，具有较强的学习能力和服务意识；',
                '5、能承受一定的工作压力。',
                '办公地点：深圳、长沙'
              ]
            }
          ]
        },
        {
          id: '10', childrenId: '2', main_title: '客服助理',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、主要负责FBA空派，海派，铁路、卡航、快递等订单的跟进与处理；',
                '2、了解公司产品与服务，及时为客户提供服务咨询以及下单，解答客户疑问；',
                '3、物流单证(报关单，提单，签收单等)的跟进与处理；',
                '4、与相关部门进行工作协调，及时解决问题；',
                '5、完成上级领导交代的其他工作。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、熟练使用办公软件操作；保证日常线上交流沟通；',
                '2、本科及以上学历，国际贸易，物流类相关专业优先；',
                '3、工作认真，细致，责任心强，具有较强的学习能力和服务意识；',
                '4、能承受一定的工作压力。',
                '办公地点：长沙'
              ]
            }
          ]
        },
        {
          id: '5', childrenId: '1', main_title: '成本结算会计',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责收集、保管代理档案资料；',
                '2、负责与代理核对账单、申请付款、核销系统等工作；',
                '3、负责成本分摊与录入系统的工作；',
                '4、负责检查成本未出具的原因，跟进代理账单的提供；',
                '5、负责检查运单亏损的原因，提醒应收费用但未收的情况；',
                '6、负责部分异常费用录入应收的工作；',
                '7、配合应收、核算等岗位完成成本相关的工作；',
                '8、负责建立、健全代理应付台账明细；',
                '9、完成上级交待的其他工作。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、一年以上成本结算岗位的工作经验，熟练使用表格',
                '2、大专及以上学历',
                '3、具备强烈的责任心和认真细致的工作作风',
                '4、诚实、稳重、细心，有主动管理意识，沟通能力强、团队协作意识好',
                '5、有物流行业工作经验优先',
                '办公地点：长沙'
              ]
            }
          ]
        },
        {
          id: '5', childrenId: '2', main_title: '应收会计',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责应收账数据录入、检查、核对、统计工作；',
                '2、出具客户账单，并协助业务进行应收款项的催收；',
                '3、做好应收回款登记工作，并及时与各个客户进行应收款项的核对工作；',
                '4、做好应收账款分析工作，对异常情况、异常数据及时追踪处理，并及时上报。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、大专以上学历，财会相关专业毕业',
                '2、能够熟练使用Word、Excel等办公软件',
                '3、良好的口头及书面表达能力，较强的服务意识',
                '4、诚实敬业、细致严谨、责任心强、原则性强',
                '5、一年以上相关岗位工作经验',
                '办公地点：长沙'
              ]
            }
          ]
        },
        {
          id: '4', childrenId: '1', main_title: '产品经理（总监）',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、熟悉海运询价、订舱、操作、放货等全流程操作细节；',
                '2、熟悉澳大利亚航线的运价构成，了解船司和同行的报价规则，有较强的议价谈判能力；',
                '3、负责海运航线产品开发与优化，以及对供应商的管理（包括引进，考核与合同续签）；',
                '4、负责航线的价格收集与报价更新,提供特殊货物的询盘/舱位确认等；',
                '5、与海运船公司进行价格和舱位的谈判，签署合作协议；',
                '6、熟悉海运出口操作流程及相关海关条例，对海运出口业内市场行情熟悉；',
                '7、有船公司任职经验或者货代公司海运渠道/市场负责人经验，有带团队经验优先；',
                '8、推动产品落地，推进用户使用，根据产品反馈，定期进行产品回顾与优化。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科学历，英语4级以上,有国际快递或者跨境进出口行业经验，有电商经验优先考虑；',
                '2、5-10年同行业工作经验，熟悉国内市场及本行业海运市场行情，有一定船东和同行、客户资源；',
                '3、对市场价格敏感、市场洞察力强，沟通协作能力良好；',
                '4、跨境物流产品市场调研以及需求分析，跨境物流产品全链路设计与产品落地。',
                '5、制定产品方案，价格方案；海外段供应商资源对接与协调；',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '4', childrenId: '2', main_title: '海运项目经理',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责美国海运航线产品开发与优化',
                '2、负责航线的价格收集与报价更新，为销售提供物流方案支持',
                '3、完成公司或上级领导交待的其它配合事宜',
                '4、协调各方资源处理突发问题'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、有传统货代商务/市场相关岗位2年以上工作经验或国际物流销售岗位2年以上经验；',
                '2、熟悉海运询价操作全流程细节，熟悉海运DDU/DDP全流程经验者优先；',
                '3、熟悉美线的运价构成，了解船司和同行的报价规则，有较强的议价谈判能力；',
                '4、对工作认真负责，有服务意识和团队精神；',
                '5、有较强抗压能力，良好的沟通能力。',
                '办公地点：深圳、广州、上海、义乌'
              ]
            }
          ]
        },
        {
          id: '2', childrenId: '1', main_title: '海运操作经理',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责熟悉海运清关-提柜-拆柜-派送等全链条港后工作的统筹、安排和分工；',
                '2、负责操作流程优化，成本控制，及供应商管理，结合各环节提高服务质量；',
                '3、把控业务流程中的关务风险，处理口岸清关、海关稽查等异常情况；',
                '4、定期汇总分析业务上出现的各类问题，并提出解决方案及建议；',
                '5、维护进出口通关的时间节点及合作方提供关键文件的时节点及相关异常处理（比如查验）；',
                '6、完成上级领导交办的其他工作；'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科及以上学历，3年及以上欧线全程操作经验，跨境物流行业优先；',
                '2、英语四级以上，熟悉欧美海运、物流及出口贸易基本知识；',
                '3、具备一定管理能力，能够妥善及时有效处理渠道各方面问题；',
                '4、具备良好的协调能力及沟通能力和良好的服务意识；',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '2', childrenId: '2', main_title: '海运操作',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责国际海运货物的操作、接收订单以及安排装柜、订舱、运输、货物跟踪、等事宜；',
                '2、协助海运操作人员完成包括审单、运单制作，信息录入；',
                '3、负责海运提单,提供海外代理补料文件及放货事宜；',
                '4、以及报关、清关文件的处理。',
                '5、跟进货物转运情况及与代理对接。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、大专及以上学历，2年以上货代操作文件工作经验；',
                '2、了解船司欧美线操作要求和流程；在海运货代庄家工作的经验；',
                '3、英语CET4级以上，有做海外操作文件经验；',
                '4、英语、德语、法语、西班牙语等会其中一种小语种优先。',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '3', childrenId: '1', main_title: '空运操作',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责国际空运货物的操作、接收订单以及安排订舱、运输、货物跟踪等事宜；',
                '2、制作空运订舱单，安排货件报关过港，与航空公司或代理订舱；',
                '3、以及报关、清关文件的处理；',
                '4、跟进货物转运情况及与代理对接。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科及以上学历，进出口贸易、物流等相关专业优先，应届毕生亦可；',
                '2、通过学习，熟悉空运操作流程，掌握了解空运市场价格以及航线知识；',
                '3、工作细心、责任心强，具有较强的服务意识和团队精神。',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '3', childrenId: '2', main_title: '2022-2024届本科生（储备干部方向）',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责跨境电商物流客服、报关、海/空运操作、销售、行政人事、财务等工作；',
                '2、与相关部门进行工作协调，及时解决问题；',
                '3、完成上级领导交代的其他工作。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科及以上学历，国际贸易，物流管理相关专业优先；',
                '2、在校期间学习认真、班级排名前15名，担任班级干部优先考虑；',
                '3、培养方向：客服、销售、项目、行政人事、财务等发展方向；',
                '4、熟练使用办公软件操作，保证日常线上交流沟通；',
                '办公地点：深圳、长沙'
              ]
            }
          ]
        },
        {
          id: '8', childrenId: '1', main_title: '海外仓客服经理',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、业务跟进：负责跟进海外仓相关业务，如收货、库存管理、退货换标、一件代发以及FBA中转等操作。',
                '2、客户沟通：与货代公司、仓库操作人员以及客户保持良好的沟通，确保服务的及时性和准确性。',
                '3、数据管理：负责收集和整理各种单证，完成送货单、订单、提单核对等工作，并进行基础数据录入和归档。',
                '4、销售推广：熟悉掌握公司海外仓储业务的优势特点，面向跨境电商平台卖家进行推广及销售。',
                '5、异常处理：在货物运送到海外仓后，负责相关的安排及协调工作，如贴标、换标工作、运费确认等。',
                '6、财务管理：负责录入海外仓账单，并及时向客户收款，确保财务流程的正确性和及时性。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科以上学历，物流/贸易相关专业更佳；',
                '2、3年以上客服工作经验，2年以上客服团队管理经验，超过5人以上的客服团队的管理经验优先；',
                '3、有电商物流、国际物流或海外仓储及大型公司客服管理相关业务操作工作经验优先；',
                '4、有以客户为中心的意识，富有责任心，工作积极主动，具有信息整合能力，学习能力佳；',
                '5、细心、周到、思维敏捷，沟通灵活，有良好的执行力和团队合作精神；',
                '6、具较强的抗压能力和协调及解决问题的能力。',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '8', childrenId: '2', main_title: '系统运维专员',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责公司内部数据库、网站的管理及维护，主要是WMS/TMS海外仓储系统；',
                '2、负责网络及办公环境的维护、管理、故障排除等日常工作；',
                '3、维护数据中心，对系统数据进行备份。',
                '4、负责在微信工作群回复客户关于系统操作的问题，故障排除；',
                '5、上级交办的其他事宜。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、熟悉信息化系统，熟悉海外仓储系统，对流程开发及优化有成熟的经验，有相关海外仓系统经验者优先；',
                '2、熟悉和掌握各种计算机软硬件，可进行安装、调试及故障排除；',
                '3、具有较强的数据和信息的敏感性，具备较强的逻辑思维能力、数据分析能力和创新意识，能够独立解决问题；',
                '4、具有较强的组织沟通能力。',
                '办公地点：深圳'
              ]
            }
          ]
        },
        {
          id: '8', childrenId: '3', main_title: '海外仓质控',
          main_content: [
            {
              title: '岗位职责：',
              content: [
                '1、负责对海外仓库中的货物进行质量监控，确保存储的商品符合质量标准。',
                '2、负责对订单发货过程进行质量控制检查，确保发货的准确性和及时性。',
                '3、协助部门主管制定和完善相关的品质管理制度与措施，并督促这些制度的实施。',
                '4、跟进仓储、运输、配送等环节的关键性能指标，并对异常数据进行处理和分析。',
                '5、负责汇总运营质量监控数据，并制作相关报表反馈给管理层。',
                '6、接收并统计内外部客户对运营环节的投诉，按月进行汇总分析，以改进服务质量。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、本科以上学历，3年以上仓储/流行业经验；',
                '2、熟悉仓储运作流程（ToB ToC电商仓）、物流质量标准、流程优化；',
                '3、具有较强的组织沟通能力。',
                '办公地点：深圳'
              ]
            }
          ]
        }
      ],

      // Campus recruitment state
      campusStep: 'categories',
      campusActiveCategoryIndex: 0,
      selectedCampusCategory: null,
      campusCategories: [
        { id: '1', title: '管培生（销售）', subtitle: '客户开拓 / 商务洽谈 / 客情维护' },
        { id: '2', title: '管培生（产品）', subtitle: '产品规划 / 市场研判 / 服务商管理' },
        { id: '3', title: '管培生（操作）', subtitle: '排仓调度 / 单证制作 / 货物跟踪' },
        { id: '4', title: '管培生（客服）', subtitle: '客户服务 / 全程跟进 / 异常处理' }
      ],
      campusJobDetail: { title: '', sections: [] },
      campusDetails: {
        '1': {
          title: '管培生（销售）',
          sections: [
            {
              title: '岗位职责：',
              content: [
                '1、通过陌拜、行业展会等多渠道开拓新客户，挖掘客户需求痛点，提供专业的物流方案，达成个人业绩指标；',
                '2、客户洽谈、报价、跟进订单，维护客情，保障出货稳定；',
                '3、维护存量客户，定期回访，建立长期稳定的合作关系；',
                '4、跟进客户对账、回款，把控应收账款周期。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、2027届统招大专及以上应届生，国贸、物流、英语、市场营销、跨境电商、管理、统计等相关专业优先；',
                '2、零基础可培养，有销售、商务或者跨境物流实习经验加分；',
                '3、擅长沟通表达，乐于主动拓展人脉，具备市场敏感度，服务意识良好；',
                '4、渴望高薪、愿意主动钻研业务，具备较强的学习能力、抗压能力和目标感。'
              ]
            }
          ]
        },
        '2': {
          title: '管培生（产品）',
          sections: [
            {
              title: '岗位职责：',
              content: [
                '1、研判国际物流市场、政策与竞品动态，识别行业风险；',
                '2、规划跨境物流产品线路，搭建价格体系，开发管理后端服务商，持续优化产品；',
                '3、开展产品宣导与内部培训，协同销售输出物流方案，助力客户开拓；',
                '4、制定产品运作 SOP，监控运营质量，管控链路风险、处理业务异常。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、2027届统招本科及硕士毕业生，国贸、物流、英语、市场营销、跨境电商、管理、统计等专业优先；',
                '2、零基础可培养，有跨境物流实习经验加分；',
                '3、具备良好的数据分析和自主学习能力，沟通逻辑清晰；',
                '4、以结果为导向，自驱力、抗压能力强。'
              ]
            }
          ]
        },
        '3': {
          title: '管培生（操作）',
          sections: [
            {
              title: '岗位职责：',
              content: [
                '1、负责排仓、提柜等调度工作，确保货物准时入仓；',
                '2、负责整理、核对货物资料，制作单证资料，确保数据一致性；',
                '3、实时跟进货物动态，及时向销售和客户同步物流节点；',
                '4、处理各类操作异常，协调各方资源推进问题解决。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、2027届统招本科及硕士毕业生，国贸、物流、英语、市场营销、跨境电商、管理、统计等专业优先；',
                '2、英语四级及以上，熟练使用办公软件；',
                '3、做事积极主动、细致高效，责任心强。'
              ]
            }
          ]
        },
        '4': {
          title: '管培生（客服）',
          sections: [
            {
              title: '岗位职责：',
              content: [
                '1、负责客户资料建档，解答客户疑问，建立稳定良好的合作关系；',
                '2、常态化跟进货物全流程节点，主动向客户同步物流动态；',
                '3、受理异常情况，联动各部门推进处理，降低客户投诉率；',
                '4、整理维护客户资料，做好数据汇总与资料归档；定期回访客户，收集客户出货需求。'
              ]
            },
            {
              title: '任职要求',
              content: [
                '1、2027届统招大专及以上毕业生，国贸、物流、英语、市场营销、跨境电商、管理、统计等专业优先；',
                '2、英语四级及以上，熟练使用办公软件；',
                '3、具备良好的沟通表达能力，做事积极主动、细致高效，责任心、服务意识强。'
              ]
            }
          ]
        }
      },

      // Resume form
      showResumeForm: false,
      resumeSubmitted: false,
      submitting: false,
      uploadedFile: null,
      campusSchoolOptions: [
        '华南农业大学',
        '广东外语外贸大学',
        '广州大学',
        '广东财经大学',
        '广州航海学院',
        '广东轻工职业技术大学',
        '广州职业技术大学',
        '广东农工商职业技术学院',
        '广东机电职业技术学院',
        '广州城建职业学院',
        '深圳技术大学',
        '深圳信息职业技术大学',
        '东莞理工学院',
        '中南林业科技大学',
        '湘潭大学',
        '湖南工商大学',
        '湖南科技大学',
        '江西财经大学',
        '华东交通大学',
        '江西理工大学',
        '江西农业大学',
        '广西民族大学',
        '南宁师范大学',
        '广西财经学院',
        '哈尔滨商业大学',
        '东北林业大学',
        '黑龙江大学',
        '哈尔滨师范大学',
        '东北农业大学'
      ],
      resumeData: {
        name: '',
        phone: '',
        education: '',
        school: '',
        major: ''
      },

      // 投递状态查询
      showStatusModal: false,
      statusQuerying: false,
      statusQueried: false,
      statusList: [],
      statusQuery: {
        name: '',
        phone: ''
      }
    };
  },
  computed: {
    socialBannerSrc() {
      return JOIN_BANNER_URLS.social;
    },
    campusBannerSrc() {
      return JOIN_BANNER_URLS.campus;
    },
    // 招聘类型：从社会/校园入口自动带入
    recruitType() {
      return this.activeTab === 'campus' ? 'CAMPUS' : 'SOCIAL';
    },
    // 应聘职位：当前详情岗位名称
    applyPosition() {
      if (this.activeTab === 'campus') {
        return (this.campusJobDetail && this.campusJobDetail.title) || '';
      }
      return (this.socialJobDetail && this.socialJobDetail.main_title) ||
        (this.selectedSocialJob && this.selectedSocialJob.title) ||
        '';
    },
    isSocialSearching() {
      return !!this.socialSearchKeyword.trim();
    },
    filteredSocialCategories() {
      // PC 不搜索，始终展示全部类别
      if (!this.isMobile) return this.inviteCategories;
      const kw = this.socialSearchKeyword.trim().toLowerCase();
      if (!kw) return this.inviteCategories;
      // 仅匹配类别名称/副标题
      return this.inviteCategories.filter(
        c => c.title.toLowerCase().includes(kw) || (c.subtitle || '').toLowerCase().includes(kw)
      );
    },
    // 移动端类别页：按岗位名称匹配具体岗位
    filteredSocialGlobalJobs() {
      if (!this.isMobile) return [];
      const kw = this.socialSearchKeyword.trim().toLowerCase();
      if (!kw) return [];
      return this.socialJobList.filter(job => (job.title || '').toLowerCase().includes(kw));
    },
    filteredCampusCategories() {
      // PC 不搜索，始终展示全部
      if (!this.isMobile) return this.campusCategories;
      const kw = this.campusSearchKeyword.trim().toLowerCase();
      if (!kw) return this.campusCategories;
      return this.campusCategories.filter(c => c.title.toLowerCase().includes(kw));
    },
    filteredSocialJobs() {
      if (!this.selectedSocialCategory) return [];
      return this.socialJobList.filter(job => job.id === this.selectedSocialCategory.id);
    },
    socialJobDetail() {
      if (!this.selectedSocialJob) return { main_title: '', main_content: [] };
      const detail = this.socialJobDetails.find(
        d => d.id === this.selectedSocialJob.id && d.childrenId === this.selectedSocialJob.childrenId
      );
      return detail || { main_title: '', main_content: [] };
    }
  },
  created() {
    this.activeTab = this.$route.params.tab === 'campus' ? 'campus' : 'social';
    preloadJoinBanners(this.activeTab);
    this.checkMobile();
    window.addEventListener('resize', this.checkMobile);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkMobile);
  },
  watch: {
    '$route.params.tab'(val) {
      this.activeTab = val === 'campus' ? 'campus' : 'social';
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 768;
    },
    switchTab(tab) {
      this.activeTab = tab;
      if (tab === 'social') {
        this.socialSearchKeyword = '';
        this.backToSocialCategories();
        this.$router.replace('/join/social');
      } else {
        this.campusStep = 'categories';
        this.selectedCampusCategory = null;
        this.campusSearchKeyword = '';
        this.$router.replace('/join/campus');
      }
    },

    // Social recruitment methods
    getSocialCategoryTitle(categoryId) {
      const category = this.inviteCategories.find(c => c.id === categoryId);
      return category ? category.title : '';
    },
    backToSocialCategories() {
      this.socialStep = 'categories';
      this.selectedSocialCategory = null;
      this.selectedSocialJob = null;
      this.socialEnteredFromSearch = false;
      this.socialListSearchKeyword = '';
    },
    backFromSocialDetail() {
      // 搜索进入详情：直接回最外层类别页；正常链路：回岗位列表
      if (this.socialEnteredFromSearch) {
        this.backToSocialCategories();
        return;
      }
      this.selectedSocialJob = null;
      this.socialStep = 'list';
    },
    selectSocialCategory(item, index) {
      this.socialActiveCategoryIndex = index;
      this.selectedSocialCategory = item;
      this.selectedSocialJob = null;
      this.socialEnteredFromSearch = false;
      this.socialListSearchKeyword = '';
      this.socialStep = 'list';
    },
    selectSocialJob(item, index) {
      this.socialActiveJobIndex = index;
      this.selectedSocialJob = item;
      this.socialEnteredFromSearch = false;
      this.socialStep = 'detail';
    },
    // 从搜索结果直接进入岗位详情，返回时回到类别页
    selectSocialJobFromSearch(item) {
      const category = this.inviteCategories.find(c => c.id === item.id) || null;
      this.selectedSocialCategory = category;
      this.socialActiveCategoryIndex = category
        ? this.inviteCategories.findIndex(c => c.id === category.id)
        : 0;
      this.selectedSocialJob = item;
      this.socialActiveJobIndex = 0;
      this.socialEnteredFromSearch = true;
      this.socialListSearchKeyword = '';
      this.socialStep = 'detail';
    },

    // Campus recruitment methods
    selectCampusCategory(item, index) {
      this.campusActiveCategoryIndex = index;
      this.selectedCampusCategory = item;
      const detail = this.campusDetails[item.id];
      if (detail) {
        this.campusJobDetail = detail;
      }
      this.campusStep = 'detail';
    },

    // Icon SVG generators
    getSocialIcon(id) {
      const icons = {
        '1': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
        '2': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12h20M2 12l4-4m-4 4l4 4"/><circle cx="12" cy="12" r="3"/></svg>',
        '3': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M2 12h20M2 12l4-4m-4 4l4 4"/><path d="M12 2v20"/></svg>',
        '4': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>',
        '5': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
        '6': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>',
        '7': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>',
        '8': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M2 12h20"/><path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"/></svg>',
        '9': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>',
        '10': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>',
        '11': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18"/><path d="M9 21V9"/></svg>',
        '12': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>',
        '13': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>'
      };
      return icons[id] || icons['1'];
    },
    getSocialListIcon(id) {
      return this.getSocialIcon(id);
    },
    getCampusIcon(id) {
      const icons = {
        // 销售
        '1': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 20V10"/><path d="M18 20V4"/><path d="M6 20v-4"/></svg>',
        // 产品
        '2': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2L2 7l10 5 10-5-10-5z"/><path d="M2 17l10 5 10-5"/><path d="M2 12l10 5 10-5"/></svg>',
        // 操作
        '3': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="1" y="3" width="15" height="13"/><polygon points="16 8 20 8 23 11 23 16 16 16 16 8"/><circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/></svg>',
        // 客服
        '4': '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>'
      };
      return icons[id] || icons['1'];
    },

    // Resume form methods
    triggerFileSelect() {
      if (this.uploadedFile) {
        this.$message.warning('只能上传 1 个文件，请先删除已上传的文件');
        return;
      }
      this.$refs.fileInput && this.$refs.fileInput.click();
    },
    setUploadedFile(file) {
      if (!file) return;
      if (file.size > 10 * 1024 * 1024) {
        this.$message.error('文件大小不能超过 10MB');
        return;
      }
      this.uploadedFile = file;
    },
    handleFileSelect(event) {
      const files = event.target.files || [];
      if (files.length > 1) {
        this.$message.warning('只能上传 1 个文件');
        event.target.value = '';
        return;
      }
      if (this.uploadedFile) {
        this.$message.warning('只能上传 1 个文件，请先删除已上传的文件');
        event.target.value = '';
        return;
      }
      this.setUploadedFile(files[0]);
      // 允许再次选择同一文件名时也能触发 change
      event.target.value = '';
    },
    removeFile() {
      this.uploadedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },
    onDragOver(event) {
      event.target.style.borderColor = '#00b4b4';
      event.target.style.background = '#f0fafa';
    },
    onDragLeave(event) {
      if (!this.uploadedFile) {
        event.target.style.borderColor = '#d9d9d9';
        event.target.style.background = '#fafafa';
      }
    },
    onDrop(event) {
      const files = event.dataTransfer.files;
      if (!files || files.length === 0) return;
      if (this.uploadedFile) {
        this.$message.warning('只能上传 1 个文件，请先删除已上传的文件');
        return;
      }
      if (files.length > 1) {
        this.$message.warning('只能上传 1 个文件');
        return;
      }
      this.setUploadedFile(files[0]);
    },
    formatFileSize(bytes) {
      if (bytes === 0) return '0 Bytes';
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },
    async submitResume() {
      if (!this.resumeData.name || !this.resumeData.phone || !this.resumeData.education || !this.resumeData.school || !this.resumeData.major) {
        this.$message.error('请填写完整的简历信息');
        return;
      }
      if (!/^1[3-9]\d{9}$/.test(this.resumeData.phone)) {
        this.$message.error('请输入正确的手机号码');
        return;
      }
      if (!this.uploadedFile) {
        this.$message.error('请上传简历附件');
        return;
      }

      this.submitting = true;
      try {
        const resumeFile = await aliOssUpload(this.uploadedFile.name, this.uploadedFile);
        await submitResume({
          name: this.resumeData.name,
          phone: this.resumeData.phone,
          education: this.resumeData.education,
          school: this.resumeData.school,
          major: this.resumeData.major,
          resumeFile: {
            fileName: resumeFile.fileName,
            fileUrl: resumeFile.fileUrl
          },
          recruitType: this.recruitType,
          applyPosition: this.applyPosition
        });
        this.resumeSubmitted = true;
      } catch (error) {
        // 业务错误已由请求拦截器统一提示；此处仅处理上传/网络等异常
        if (error && error.message === 'Upload failed') {
          this.$message.error('简历上传失败，请稍后重试');
        } else if (!error || error.code === undefined) {
          this.$message.error('投递失败，请稍后重试');
        }
      } finally {
        this.submitting = false;
      }
    },
    openResumeModal() {
      this.resumeSubmitted = false;
      this.showResumeForm = true;
    },
    closeResumeModal() {
      this.showResumeForm = false;
      setTimeout(() => {
        this.resumeSubmitted = false;
        this.resetResumeForm();
      }, 300);
    },
    resetResumeForm() {
      this.resumeData = {
        name: '',
        phone: '',
        education: '',
        school: '',
        major: ''
      };
      this.uploadedFile = null;
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = '';
      }
    },

    // 投递状态查询
    openStatusModal() {
      this.showStatusModal = true;
    },
    closeStatusModal() {
      this.showStatusModal = false;
      setTimeout(() => {
        this.statusQueried = false;
        this.statusList = [];
        this.statusQuery = { name: '', phone: '' };
        this.statusQuerying = false;
      }, 300);
    },
    async queryResumeStatus() {
      const name = (this.statusQuery.name || '').trim();
      const phone = (this.statusQuery.phone || '').trim();
      if (!name || !phone) {
        this.$message.error('请填写姓名和手机号');
        return;
      }
      if (!/^1[3-9]\d{9}$/.test(phone)) {
        this.$message.error('请输入正确的手机号码');
        return;
      }

      this.statusQuerying = true;
      try {
        const res = await querySelfResumeStatus({ name, phone });
        const data = res && res.data;
        if (Array.isArray(data)) {
          this.statusList = data;
        } else if (data && Array.isArray(data.list)) {
          this.statusList = data.list;
        } else if (data && Array.isArray(data.records)) {
          this.statusList = data.records;
        } else {
          this.statusList = [];
        }
        this.statusQueried = true;
      } catch (error) {
        if (!error || error.code === undefined) {
          this.$message.error('查询失败，请稍后重试');
        }
      } finally {
        this.statusQuerying = false;
      }
    },
    formatHandleStatus(status) {
      const map = {
        PENDING: '待处理',
        VIEWED: '已查看',
        INTERVIEWING: '面试中',
        HIRED: '已录用',
        ELIMINATED: '未通过'
      };
      return map[status] || status || '未知';
    },
    formatRecruitType(type) {
      const map = {
        SOCIAL: '社会招聘',
        CAMPUS: '校园招聘'
      };
      return map[type] || type || '-';
    },
    formatCreateTime(time) {
      if (!time) return '-';
      const str = String(time).replace('T', ' ');
      return str.length > 19 ? str.slice(0, 19) : str;
    }
  }
};
</script>

<style scoped>
/* 覆盖全局 styles.css 的 content-box !important，避免 width:100% + padding 撑破布局 */
.join-page,
.join-page * {
  box-sizing: border-box !important;
}

.join-page h1,
.join-page h2,
.join-page h3,
.join-page h4,
.join-page p,
.join-page ul,
.join-page ol,
.join-page li {
  margin: 0;
  padding: 0;
}

.join-page {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'PingFang SC', 'Hiragino Sans GB', 'Microsoft YaHei', sans-serif;
  background-color: #f5f7fa;
  color: #333;
  min-height: 100%;
  padding-bottom: 24px;
}

/* Banner 区域：与关于我们等页一致，宽度 70%，高度按原图比例 */
.banner {
  position: relative;
  line-height: 0;
  background: #fff;
}

.banner-img {
  display: block;
  width: 70%;
  height: auto;
  flex-shrink: 0;
}

/* Tab 导航 */
.tab-nav {
  max-width: 1200px;
  margin: -30px auto 0;
  padding: 0 24px;
  position: relative;
  z-index: 10;
}

.tab-container {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  display: flex;
  overflow: hidden;
}

.tab-item {
  flex: 1;
  padding: 20px 0;
  text-align: center;
  cursor: pointer;
  font-size: 16px;
  color: #666;
  transition: all 0.3s;
  border: none;
  background: #fff;
}

.tab-item:hover {
  color: #00b4b4;
  background: #f8fafa;
}

.tab-item.active {
  background: #24303E;
  color: #fff;
}

/* 内容区域 */
.content {
  max-width: 1200px;
  margin: 40px auto 0;
  padding: 0 24px 40px;
}

/* 搜索结果分组 */
.search-result-group + .search-result-group {
  margin-top: 28px;
}

/* 提高优先级，避免被 .join-page h3 { margin: 0 } 覆盖 */
.join-page .search-result-title {
  font-size: 15px;
  font-weight: 600;
  color: #666;
  margin: 0 0 16px;
  line-height: 1.4;
}

/* 职位卡片网格 */
.job-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.job-card {
  background: #fff;
  border-radius: 12px;
  padding: 24px;
  display: flex;
  align-items: center;
  gap: 16px;
  cursor: pointer;
  transition: all 0.3s;
  border: 1px solid transparent;
}

.job-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 24px rgba(0, 180, 180, 0.12);
  border-color: rgba(0, 180, 180, 0.2);
}

.job-icon {
  width: 48px;
  height: 48px;
  background: #f0fafa;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: #00b4b4;
}

.job-icon svg {
  width: 24px;
  height: 24px;
}

.job-info h3 {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.job-info p {
  font-size: 13px;
  color: #999;
  margin-top: 4px;
}

/* 返回链接 */
.back-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  color: #666;
  text-decoration: none;
  font-size: 14px;
  margin-bottom: 24px;
  cursor: pointer;
  transition: color 0.3s;
}

.back-link:hover {
  color: #00b4b4;
}

.back-link svg {
  width: 16px;
  height: 16px;
}

/* 内容卡片 */
.content-card {
  background: #fff;
  border-radius: 12px;
  padding: 40px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
}

/* 提高优先级，避免被 .join-page h1 { margin: 0 } 覆盖 */
.join-page .job-title {
  font-size: 28px;
  font-weight: 600;
  color: #333;
  margin: 0 0 36px;
  line-height: 1.35;
}

/* 避免与全局 styles.css 的 .section / .section-title 冲突 */
.job-section {
  margin-bottom: 32px;
  padding: 0;
}

.job-section:last-of-type {
  margin-bottom: 0;
}

.join-page .job-section-title {
  font-size: 18px;
  font-weight: 600;
  color: #333;
  margin: 0 0 14px;
  padding: 0;
  text-transform: none;
  letter-spacing: normal;
  line-height: 1.4;
}

.job-section-content {
  color: #666;
  font-size: 15px;
  line-height: 1.8;
}

.job-section-content ol {
  padding-left: 20px;
  margin: 0;
}

.job-section-content li {
  margin-bottom: 8px;
}

.job-section-content li:last-child {
  margin-bottom: 0;
}

/* 投递按钮 */
.apply-section {
  margin-top: 40px;
  padding-top: 30px;
  border-top: 1px solid #eee;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.apply-btn {
  background: linear-gradient(135deg, #00b4b4 0%, #00d4d4 100%);
  color: #fff;
  border: none;
  padding: 14px 40px;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  box-sizing: border-box !important;
  min-width: 148px;
}

.apply-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 180, 180, 0.3);
}

.apply-btn-secondary {
  background: #fff;
  color: #00b4b4;
  border: 1px solid #00b4b4;
  box-shadow: none;
}

.apply-btn-secondary:hover {
  background: #f0fafa;
  box-shadow: 0 6px 16px rgba(0, 180, 180, 0.15);
}

/* 搜索框 */
.search-box {
  width: 100%;
  max-width: 500px;
  margin: 0 auto 36px;
  box-sizing: border-box !important;
  min-width: 0;
}

.search-box >>> .el-input,
.search-box >>> .el-input__inner {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box !important;
}

.search-box >>> .el-input__inner {
  height: 44px;
  line-height: 44px;
  border-radius: 22px;
  font-size: 14px;
  border-color: #d9d9d9;
}

.search-box >>> .el-input__inner:focus {
  border-color: #00b4b4;
}

/* 空状态 */
.empty-state {
  text-align: center;
  padding: 120px 20px;
  color: #999;
  font-size: 16px;
}

/* 投递弹窗 */
.modal-overlay {
  display: none;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 2100;
  align-items: center;
  justify-content: center;
  padding: 20px;
}

.modal-overlay.active {
  display: flex;
}

.join-modal {
  display: block;
  position: relative;
  background: #fff;
  border-radius: 16px;
  width: 100%;
  max-width: 560px;
  max-height: 90vh;
  overflow-x: hidden;
  overflow-y: auto;
  animation: modalSlideIn 0.3s ease;
  box-sizing: border-box !important;
}

@keyframes modalSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.modal-header {
  padding: 24px 24px 0;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.modal-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
}

.modal-close {
  width: 32px;
  height: 32px;
  border: none;
  background: #f5f5f5;
  border-radius: 50%;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.3s;
}

.modal-close:hover {
  background: #eee;
}

.modal-close svg {
  width: 16px;
  height: 16px;
  color: #666;
}

.modal-body {
  padding: 24px;
}

/* 表单 */
.form-group {
  margin-bottom: 20px;
}

.form-label {
  display: block;
  font-size: 14px;
  color: #333;
  margin-bottom: 8px;
  font-weight: 500;
}

.form-label .required {
  color: #ff4d4f;
  margin-left: 4px;
}

.form-input,
.form-select {
  width: 100%;
  padding: 12px 16px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  font-size: 14px;
  transition: all 0.3s;
  background: #fff;
  outline: none;
  font-family: inherit;
  box-sizing: border-box !important;
  display: block;
}

.form-input:focus,
.form-select:focus {
  border-color: #00b4b4;
  box-shadow: 0 0 0 3px rgba(0, 180, 180, 0.1);
}

.form-input::placeholder {
  color: #bfbfbf;
}

.form-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
}

/* 文件上传 */
.upload-area {
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  padding: 32px;
  text-align: center;
  cursor: pointer;
  transition: all 0.3s;
  background: #fafafa;
}

.upload-area:hover {
  border-color: #00b4b4;
  background: #f0fafa;
}

.upload-area.has-file {
  border-color: #00b4b4;
  background: #f0fafa;
}

.upload-icon {
  width: 48px;
  height: 48px;
  margin: 0 auto 12px;
  color: #00b4b4;
}

.upload-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 4px;
}

.upload-text span {
  color: #00b4b4;
  font-weight: 500;
}

.upload-hint {
  font-size: 12px;
  color: #999;
  margin-top: 8px;
}

.file-input {
  display: none;
}

.file-info {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  background: #f0fafa;
  border-radius: 8px;
  margin-top: 12px;
}

.file-icon {
  width: 40px;
  height: 40px;
  background: #00b4b4;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-icon svg {
  width: 20px;
  height: 20px;
  color: #fff;
}

.file-details {
  flex: 1;
}

.file-name {
  font-size: 14px;
  color: #333;
  font-weight: 500;
  word-break: break-all;
}

.file-size {
  font-size: 12px;
  color: #999;
}

.file-remove {
  width: 24px;
  height: 24px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #999;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.file-remove:hover {
  color: #ff4d4f;
}

/* 提交按钮 */
.submit-btn {
  width: 100%;
  background: linear-gradient(135deg, #00b4b4 0%, #00d4d4 100%);
  color: #fff;
  border: none;
  padding: 14px;
  font-size: 16px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  margin-top: 8px;
  box-sizing: border-box !important;
  display: block;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 20px rgba(0, 180, 180, 0.3);
}

.submit-btn:disabled {
  background: #ccc;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

/* 成功提示 */
.success-message {
  text-align: center;
  padding: 40px 24px;
}

.success-icon {
  width: 64px;
  height: 64px;
  background: #52c41a;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;
}

.success-icon svg {
  width: 32px;
  height: 32px;
  color: #fff;
}

.success-title {
  font-size: 20px;
  font-weight: 600;
  color: #333;
  margin-bottom: 12px;
}

.success-text {
  font-size: 14px;
  color: #666;
  margin-bottom: 24px;
}

.success-btn {
  background: #f5f5f5;
  color: #333;
  border: none;
  padding: 12px 32px;
  font-size: 14px;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
}

.success-btn:hover {
  background: #eee;
}

/* 投递状态查询 */
.status-modal {
  max-width: 640px;
}

.status-tip {
  margin: 0 0 20px;
  padding: 12px 14px;
  background: #f5fbfb;
  border-radius: 8px;
  color: #666;
  font-size: 13px;
  line-height: 1.6;
}

.status-query-form {
  margin-bottom: 8px;
}

.status-query-fields {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 16px;
}

.status-query-btn {
  margin-top: 0;
}

.status-result {
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.status-result-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 15px;
  font-weight: 600;
  color: #333;
  margin-bottom: 14px;
}

.status-result-title span {
  font-size: 13px;
  font-weight: 400;
  color: #999;
}

.status-empty {
  text-align: center;
  padding: 36px 16px;
  background: #fafafa;
  border-radius: 10px;
  color: #666;
  font-size: 14px;
}

.status-empty-hint {
  margin-top: 8px;
  color: #999;
  font-size: 13px;
}

.status-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.status-card {
  border: 1px solid #eef0f2;
  border-radius: 12px;
  background: #fff;
  overflow: hidden;
}

.status-card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  padding: 16px;
  background: #f8fbfb;
  border-bottom: 1px solid #eef0f2;
}

.status-card-main {
  min-width: 0;
  flex: 1;
}

.status-position {
  font-size: 16px;
  font-weight: 600;
  color: #222;
  line-height: 1.4;
  word-break: break-word;
}

.status-meta {
  margin-top: 6px;
  font-size: 12px;
  color: #999;
  line-height: 1.4;
}

.status-tag {
  flex-shrink: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 64px;
  padding: 4px 10px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 500;
  line-height: 1.4;
  background: #f0f0f0;
  color: #666;
}

.status-pending {
  background: #fff7e6;
  color: #d48806;
}

.status-viewed {
  background: #e6f4ff;
  color: #1677ff;
}

.status-interviewing {
  background: #f0f5ff;
  color: #2f54eb;
}

.status-hired {
  background: #f6ffed;
  color: #52c41a;
}

.status-eliminated {
  background: #fff1f0;
  color: #ff4d4f;
}

.status-card-body {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px 16px;
  padding: 14px 16px 16px;
}

.status-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}

.status-row span {
  font-size: 12px;
  color: #999;
}

.status-row em {
  font-style: normal;
  font-size: 14px;
  color: #333;
  word-break: break-word;
  line-height: 1.4;
}

/* ============== 响应式 ============== */
@media (max-width: 768px) {
  .tab-nav {
    margin: -20px auto 0;
  }

  .tab-item {
    padding: 16px 0;
    font-size: 15px;
  }

  .join-page {
    padding-bottom: 32px;
  }

  .content {
    margin: 24px auto 0;
    padding: 0 16px 48px;
  }

  /* 移动端单列布局 */
  .job-grid {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .job-card {
    padding: 20px 16px;
  }

  .job-icon {
    width: 40px;
    height: 40px;
    border-radius: 8px;
  }

  .job-icon svg {
    width: 20px;
    height: 20px;
  }

  .job-info h3 {
    font-size: 15px;
  }

  .job-info p {
    font-size: 12px;
  }

  /* 内容卡片 */
  .content-card {
    padding: 24px 20px 28px;
  }

  .join-page .job-title {
    font-size: 22px;
    margin: 0 0 28px;
  }

  .job-section {
    margin-bottom: 28px;
  }

  .join-page .job-section-title {
    font-size: 16px;
    margin: 0 0 12px;
  }

  .job-section-content {
    font-size: 14px;
    line-height: 1.7;
  }

  .apply-section {
    width: 100%;
    margin-top: 24px;
    padding-top: 20px;
    flex-direction: column;
    gap: 10px;
  }

  /* 表单移动端 */
  .form-row {
    grid-template-columns: 1fr;
    gap: 0;
  }

  .join-modal {
    max-height: 95vh;
    margin: 0;
    max-width: 100%;
    box-sizing: border-box !important;
  }

  .modal-header {
    padding: 20px 16px 0;
  }

  .modal-body {
    padding: 16px;
  }

  .form-input,
  .form-select {
    padding: 10px 14px;
    font-size: 14px;
  }

  .upload-area {
    padding: 24px 16px;
    box-sizing: border-box !important;
  }

  .submit-btn {
    padding: 12px;
    font-size: 15px;
  }

  .apply-btn,
  .apply-btn-secondary {
    width: 100%;
    max-width: 100%;
    min-width: 0;
    padding: 12px;
    font-size: 15px;
  }

  .status-modal {
    max-width: 100%;
  }

  .status-query-fields {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .status-card-header {
    flex-direction: column;
    align-items: stretch;
    gap: 10px;
  }

  .status-tag {
    align-self: flex-start;
  }

  .status-card-body {
    grid-template-columns: 1fr;
    gap: 10px;
  }

  /* 搜索框 */
  .search-box {
    width: 100%;
    max-width: 100%;
    margin: 0 0 24px;
  }

  .join-page .search-result-title {
    margin: 0 0 14px;
  }

  .search-result-group + .search-result-group {
    margin-top: 24px;
  }
}

@media (max-width: 480px) {
  .tab-item {
    padding: 14px 0;
    font-size: 14px;
  }

  .job-card {
    padding: 16px 12px;
    gap: 12px;
  }

  .job-icon {
    width: 36px;
    height: 36px;
  }

  .job-icon svg {
    width: 18px;
    height: 18px;
  }

  .job-info h3 {
    font-size: 14px;
  }

  .job-info p {
    font-size: 11px;
  }

  .job-title {
    font-size: 20px;
  }
}
</style>
